import { ICommonSettings, IFirecrawlMcpSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigFirecrawlMcpSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IFirecrawlMcpSetting;
}

export interface IConfigFirecrawlMcpSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingFirecrawlMcpId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigFirecrawlMcpSettings extends React.PureComponent<
  IConfigFirecrawlMcpSettingsProps,
  IConfigFirecrawlMcpSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigFirecrawlMcpSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigFirecrawlMcpSettingsProps & IWithSDProps) {
    if (prevProps.editingFirecrawlMcpId !== this.props.editingFirecrawlMcpId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingFirecrawlMcpId) return;

    const editingConfig = this.props.commonSettings.firecrawlMcpSettings?.find(
      (x) => x.id === this.props.editingFirecrawlMcpId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const firecrawlMcpSetting = this.props.commonSettings?.firecrawlMcpSettings;
    if (!firecrawlMcpSetting) return;

    const isSettingAlreadyExist = firecrawlMcpSetting.some(
      (x) => x.name === value && x.id !== this.props.editingFirecrawlMcpId
    );

    if (isSettingAlreadyExist) throw new Error("Firecrawl Mcp Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IFirecrawlMcpSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      apiKey: data.apiKey
    };

    if (this.props.editingFirecrawlMcpId) {
      obj.id = this.props.editingFirecrawlMcpId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "firecrawlMcpSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "firecrawlMcpSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingFirecrawlMcpId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingFirecrawlMcpId || "",
      configKey: "firecrawlMcpSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Firecrawl Mcp setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Firecrawl Mcp setting"
        secondHeader="Firecrawl Mcp setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingFirecrawlMcpId ? this.deleteSetting : undefined}
            deleteTestId="mcpFirecrawlSettingDeleteButton"
            submitTestId="mcpFirecrawlSettingCreateButton"
            submitLabel={this.props.editingFirecrawlMcpId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configFirecrawlForm"
          />
        }
      >
        <Form
          id="configFirecrawlForm"
          name="firecrawlMcpSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("mcp_firecrawlmcpsetting_description")}>
            <ConfigField label="Name" required tooltipContentKey="mcp_firecrawlmcpsetting_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Firecrawl Mcp Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Firecrawl Mcp Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="mcpFirecrawlSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="API Key" required tooltipContentKey="mcp_firecrawlmcpsetting_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Filecrawl Mcp API Key." }]}>
                <KuikaInput size="md" data-testid="mcpFirecrawlSettingApiKeyInput" autoFocus />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
